// file: src/components/AgentModeDropdown.jsx

import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, ChevronUp, Zap, MessageSquare, Target, Cloud, Cpu, Layers } from 'lucide-react';
import { AGENT_TYPES, AGENT_LABELS, AGENT_DESCRIPTIONS, getMCPStatus } from '../services/mcpServices';
import styles from './AgentModeDropdown.module.css';

/**
 * Agent mode dropdown designed to fit inline in the chat input area.
 * Displays only the icon and label in a pill-style button.
 * Descriptions are shown on hover via tooltips.
 */
const AgentModeDropdown = ({ selectedMode, onModeChange, disabled = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [mcpStatus, setMcpStatus] = useState({});
  const [loading, setLoading] = useState(true);
  const dropdownRef = useRef(null);

  // Load MCP status on component mount
  useEffect(() => {
    async function loadMCPStatus() {
      try {
        setLoading(true);
        const status = await getMCPStatus();
        setMcpStatus(status);
      } catch (error) {
        console.error('Failed to load MCP status:', error);
        // Set default status if API fails
        setMcpStatus({
          blender: { available: true },
          freecad: { available: false },
          jupyterlab: { available: false },
          kicad: { available: false }
        });
      } finally {
        setLoading(false);
      }
    }

    loadMCPStatus();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Get icon for each agent type
  const getAgentIcon = (agentType) => {
    switch (agentType) {
      case AGENT_TYPES.ASK:
        return <MessageSquare size={14} />;
      case AGENT_TYPES.BLENDER:
        return <Zap size={14} />;
      case AGENT_TYPES.FREECAD:
        return <Target size={14} />;
      case AGENT_TYPES.JUPYTERLAB:
        return <Cpu size={14} />;
      case AGENT_TYPES.KICAD:
        return <Layers size={14} />;
      default:
        return <MessageSquare size={14} />;
    }
  };

  // Check if an agent is available
  // Agents are grayed out when their corresponding MCP server is not available/running
  // "Ask" mode is always available as it doesn't require an MCP server
  const isAgentAvailable = (agentType) => {
    if (agentType === AGENT_TYPES.ASK) return true; // Ask mode is always available
    return mcpStatus[agentType]?.available || false;
  };

  // Handle mode selection
  const handleModeSelect = (mode) => {
    if (disabled || !isAgentAvailable(mode)) return;
    
    onModeChange(mode);
    setIsOpen(false);
  };

  // Toggle dropdown
  const toggleDropdown = () => {
    if (disabled) return;
    setIsOpen(!isOpen);
  };

  const currentLabel = AGENT_LABELS[selectedMode] || 'Ask';
  const currentDescription = AGENT_DESCRIPTIONS[selectedMode] || 'Ask questions using knowledge bases';

  return (
    <div className={styles.agentModeContainer} ref={dropdownRef}>
      <button
        className={`${styles.agentModeButton} ${isOpen ? styles.open : ''} ${disabled ? styles.disabled : ''}`}
        onClick={toggleDropdown}
        disabled={disabled}
        title={currentDescription}
      >
        <div className={styles.agentModeIcon}>
          {getAgentIcon(selectedMode)}
        </div>
        <div className={styles.agentModeLabel}>{currentLabel}</div>
        <div className={styles.agentModeArrow}>
          {isOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
        </div>
      </button>

      {isOpen && (
        <div className={styles.dropdownMenu}>
          {Object.entries(AGENT_LABELS).map(([agentType, label]) => {
            // agentType is already the value like 'ask', 'blender', etc.
            const available = isAgentAvailable(agentType);
            const isSelected = selectedMode === agentType;
            const description = AGENT_DESCRIPTIONS[agentType];
            const tooltipText = available 
              ? description 
              : `${description} (Server not available)`;
            
            return (
              <button
                key={agentType}
                type="button"
                className={`${styles.dropdownItem} ${isSelected ? styles.selected : ''} ${!available ? styles.disabled : ''}`}
                onClick={() => handleModeSelect(agentType)}
                aria-disabled={!available} // Use aria-disabled instead of disabled
                title={tooltipText} // Browser tooltip
                data-tooltip={tooltipText} // Backup for custom tooltip if needed
              >
                <div className={styles.dropdownItemIcon}>
                  {getAgentIcon(agentType)}
                </div>
                <div className={styles.dropdownItemLabel}>{label}</div>
                {!available && (
                  <div className={styles.unavailableBadge} title="Server not running">
                    <Cloud size={10} />
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AgentModeDropdown;

